import { NgIf, NgFor, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardHeader, MatCardContent } from '@angular/material/card';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggleChange, MatSlideToggle } from '@angular/material/slide-toggle';
import { LocationSilo, OrderingInformation, Silo, SiloDistributionRequirements, SiloDistributionTypes } from '@next/core-lib/cart';
import { AgrEnvironment, EnvironmentInjector } from '@next/core-lib/environment';
import { AgrFormModule } from '@next/core-lib/form';
import { AgrI18nSharedModule } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { SessionSelectors } from '@next/core-lib/session';
import { SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, map, shareReplay, skipWhile, startWith, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { SiloFormEntry } from '../../models/silo.models';
import { FormatSiloTitlePipe } from '../../pipes/format-silo-title.pipe';
import * as ProductSelectors from '../../store/selectors';

@Component({
  selector: 'app-product-silo',
  templateUrl: './product-silo.component.html',
  styleUrls: ['./product-silo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    MatIcon,
    MatSlideToggle,
    FormsModule,
    ReactiveFormsModule,
    NgFor,
    MatCard,
    MatCardHeader,
    MatCardContent,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    AgrFormModule,
    MatSuffix,
    MatButton,
    NgTemplateOutlet,
    MatProgressSpinner,
    AsyncPipe,
    AgrI18nSharedModule,
    FormatSiloTitlePipe,
  ],
})
export class ProductSiloComponent implements OnInit, OnDestroy {
  @Input() orderingInformation: OrderingInformation | undefined;

  @Output() silosChange = new EventEmitter<Silo[]>();
  @Output() silosAreValid = new EventEmitter<boolean>();
  @Output() silosAreOptional = new EventEmitter<boolean>();

  readonly sessionUrl$ = this.store.select(SessionSelectors.selectUrlWithSession({ url: '' }));

  exact: FormControl = new FormControl(false);
  entries: FormArray = new FormArray([]);

  locationSilos$: Observable<LocationSilo[]> = this.store.select(ProductSelectors.selectSilos);
  silosLoaded$: Observable<boolean> = this.store.select(ProductSelectors.selectSilosLoaded);
  silosNotAvailable$: Observable<boolean> = this.store.select(ProductSelectors.selectSilosNotAvailable);

  availableFullSilos$: Observable<LocationSilo[]> = combineLatest([this.locationSilos$, this.entries.valueChanges.pipe(startWith([]))]).pipe(
    map(([silos, _]) => silos.filter((silo) => (this.entries.controls?.[0]?.value as SiloFormEntry)?.rest?.id !== silo?.id)),
  );

  availableRestSilos$: Observable<LocationSilo[]> = combineLatest([this.locationSilos$, this.entries.valueChanges.pipe(startWith([]))]).pipe(
    map(([silos, _]) => silos.filter((silo) => (this.entries.controls?.[0]?.value as SiloFormEntry)?.full?.id !== silo?.id)),
  );

  quantitySum$: Observable<number> = this.entries.valueChanges.pipe(
    startWith([]),
    shareReplay(1),
    map((value: SiloFormEntry[]) =>
      value.reduce((sum, entry) => {
        const parsedQuantity = typeof entry?.quantity === 'string' ? parseInt(entry.quantity, 10) : entry.quantity;
        if (!isNaN(parsedQuantity)) {
          return sum + parsedQuantity;
        }

        return sum;
      }, 0),
    ),
  );

  quantityInTheLine: number;
  quantityExceededOrder$: Observable<boolean>;
  lessThanQuantity$: Observable<boolean>;
  toBeDistributedQuantity$: Observable<number>;
  isLtSm$ = this.breakpointService.ltSm$;
  quantity$: Observable<number>;

  public isExactWeightShipTo: boolean;
  private destroy$: Subject<void> = new Subject<void>();
  private quantitySubject = new ReplaySubject<number>(1);
  private selectedSilosSubject = new ReplaySubject<Silo[]>();
  private readonly location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);

  constructor(
    private readonly store: Store,
    @Inject(EnvironmentInjector) readonly env: AgrEnvironment,
    @Inject(BreakpointServiceInjector) private readonly breakpointService: BreakpointService,
  ) {
    this.entries.valueChanges
      .pipe(
        debounceTime(100),
        takeUntil(this.destroy$),
        tap(() => {
          this.processSiloChange();
        }),
      )
      .subscribe();

    this.quantityExceededOrder$ = combineLatest([this.quantitySum$, this.quantitySubject]).pipe(map(([quantitySum, quantity]) => quantitySum > quantity));
    this.lessThanQuantity$ = combineLatest([this.quantitySum$, this.quantitySubject]).pipe(map(([quantitySum, quantity]) => quantitySum < quantity));
    this.toBeDistributedQuantity$ = combineLatest([this.quantitySum$, this.quantitySubject]).pipe(map(([quantitySum, quantity]) => quantity - quantitySum));
    this.quantity$ = this.quantitySubject.asObservable();

    this.location$
      .pipe(
        map((l) => l?.exactWeighing || false),
        takeUntil(this.destroy$),
      )
      .subscribe((isExact) => (this.isExactWeightShipTo = isExact));

    this.toBeDistributedQuantity$.pipe(takeUntil(this.destroy$)).subscribe((quantity) => (this.quantityInTheLine = quantity));
  }

  @Input()
  set quantity(quantity: number) {
    this.quantitySubject.next(quantity);
  }

  @Input()
  set selectedSilos(silos: Silo[]) {
    this.selectedSilosSubject.next(silos);
  }

  ngOnInit() {
    combineLatest([this.quantityExceededOrder$, this.lessThanQuantity$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([quantityExceeded, lessThanQuantity]) => {
        const exact = this.exact.value;
        const siloConfigurationValid =
          !exact || this.entries.controls.every(({ value: { silo, quantity } }) => (quantity === null && silo === null) || (+quantity > 0 && silo !== null));

        this.silosAreValid.emit((quantityExceeded === false && lessThanQuantity === false && siloConfigurationValid) || exact === false);
      });

    this.locationSilos$
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(this.selectedSilosSubject),
        skipWhile(([locationSilos, selectedSilos]) => !locationSilos?.length || !selectedSilos),
        take(1),
      )
      .subscribe(([silos, selectedSilos]) => {
        if (selectedSilos?.length > 0) {
          const isExact = selectedSilos[0].distributionType === SiloDistributionTypes.exact;
          this.exact.setValue(isExact);

          if (isExact) {
            selectedSilos.forEach((selectedSilo) => {
              const entry = this.getDefaultEntry(isExact);
              entry.get('silo').setValue(silos.find((silo) => silo.id === selectedSilo.id));
              entry.get('quantity').setValue(selectedSilo.quantity);
              this.addEntry(isExact, entry);
            });
          } else {
            const fullSilo = selectedSilos.find((selectedSilo) => selectedSilo.distributionType === SiloDistributionTypes.full);
            const restSilo = selectedSilos.find((selectedSilo) => selectedSilo.distributionType === SiloDistributionTypes.leftover);

            const entry = this.getDefaultEntry(isExact);
            if (fullSilo) {
              entry.get('full').setValue(silos.find((silo) => silo.id === fullSilo.id));
            }
            if (restSilo) {
              entry.get('rest').setValue(silos.find((silo) => silo.id === restSilo.id));
            }
            this.addEntry(isExact, entry);
          }
        } else {
          this.exact.setValue(false);
          this.addEntry(false);
        }
      });

    this.exact.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((newValue) => {
      if (this.orderingInformation?.siloDistributionRequirements === SiloDistributionRequirements.optional) {
        this.silosAreOptional.emit(!newValue);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addEntry(exact?: boolean, entry?: FormGroup): void {
    this.entries.push(entry || this.getDefaultEntry(exact));
  }

  changeExact(event: MatSlideToggleChange): void {
    this.entries.clear();
    this.addEntry(event.source.checked);
  }

  getAvailableExactSilos(silos: LocationSilo[], group: FormGroup): LocationSilo[] | false {
    const availableExactSilos = silos.filter(
      (silo) =>
        silo?.id === (group.get('silo').value as Silo)?.id || !(this.entries.value as SiloFormEntry[])?.find((exactSilo) => exactSilo?.silo?.id === silo?.id),
    );

    return availableExactSilos.length > 0 ? availableExactSilos : false;
  }

  getDefaultEntry(exact?: boolean): FormGroup {
    return new FormGroup(
      !!exact || !!this.exact.value
        ? {
            silo: new FormControl(),
            quantity: new FormControl(),
          }
        : {
            full: new FormControl(),
            rest: new FormControl(),
          },
    );
  }

  getFormGroup(control: AbstractControl): FormGroup {
    const group = control as FormGroup;

    if (group && Object.keys(group.controls).length > 0) {
      return group;
    }
  }

  removeEntry(index: number): void {
    this.entries.removeAt(index);
  }

  private processSiloChange(): void {
    let silos: Silo[] = [];

    if (this.exact.value && this.entries.length > 0) {
      silos = this.getExactSilos();
    } else if (this.entries.length > 0) {
      silos = this.getNonExactSilos(this.quantityInTheLine);
    }

    this.silosChange.emit(silos);
  }

  private getExactSilos(): Silo[] {
    return (this.entries.value as SiloFormEntry[])
      .filter((entry: SiloFormEntry) => !!entry.silo && !!entry.quantity)
      .map((entry: SiloFormEntry) => ({
        id: entry.silo.id,
        siloNumber: entry?.silo?.siloNumber,
        name: undefined,
        quantity: typeof entry.quantity === 'string' ? parseInt(entry.quantity, 10) : entry.quantity,
        distributionType: SiloDistributionTypes.exact,
      }));
  }

  private getNonExactSilos(quantityInTheLine: number): Silo[] {
    const silos: Silo[] = [];
    const firstEntry = this.getFormGroup(this.entries.controls[0]).value as SiloFormEntry;

    if (firstEntry.full && firstEntry.rest) {
      silos.push(this.createSilo(firstEntry.full, SiloDistributionTypes.full));
      silos.push(this.createSilo(firstEntry.rest, SiloDistributionTypes.leftover));
    } else if (firstEntry.full) {
      // Single full silo will become exact if a rest silo is not defined.
      silos.push(this.createSilo(firstEntry.full, SiloDistributionTypes.exact, quantityInTheLine));
    }

    return silos;
  }

  private createSilo(silo: Silo, distributionType: SiloDistributionTypes, quantity?: number): Silo {
    return {
      id: silo.id,
      siloNumber: silo.siloNumber,
      name: silo.name,
      quantity,
      distributionType,
    };
  }
}
